/**
 * Confirmation dialog for deleting a recipe.
 * Returns a Promise that resolves true if confirmed, false if cancelled.
 */
export class RecipeDeleteDialog {
  private modalElement: HTMLElement | null = null;
  private resolvePromise: ((value: boolean) => void) | null = null;
  private recipeTitle: string;

  constructor(recipeTitle: string) {
    this.recipeTitle = recipeTitle;
  }

  /**
   * Open the dialog and return a promise that resolves with the user's choice.
   */
  async open(): Promise<boolean> {
    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      this.createModal();
    });
  }

  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className =
      'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
    this.modalElement.id = 'recipe-delete-dialog';

    const content = document.createElement('div');
    content.className =
      'bg-[#1e1e2e] rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl border border-red-900/50';

    // Header
    const header = document.createElement('h2');
    header.className = 'text-xl font-semibold mb-2 text-red-400';
    header.textContent = 'Esborra la recepta';
    content.appendChild(header);

    // Message
    const message = document.createElement('p');
    message.className = 'text-[#a0a0a0] mb-1';
    message.textContent = `Esborrar "${this.recipeTitle}"?`;
    content.appendChild(message);

    const subtext = document.createElement('p');
    subtext.className = 'text-[#888] text-sm mb-4';
    subtext.textContent = 'Aquesta acció no es pot desfer.';
    content.appendChild(subtext);

    // Buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'flex gap-3';

    const deleteBtn = document.createElement('button');
    deleteBtn.id = 'recipe-delete-confirm-btn';
    deleteBtn.className =
      'flex-1 bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500';
    deleteBtn.textContent = 'Esborra';

    const cancelBtn = document.createElement('button');
    cancelBtn.id = 'recipe-delete-cancel-btn';
    cancelBtn.className =
      'flex-1 bg-[#333] text-[#e0e0e0] px-4 py-2 rounded-md hover:bg-[#444] focus:outline-none focus:ring-2 focus:ring-[#555]';
    cancelBtn.textContent = 'Cancel·la';

    buttonGroup.appendChild(deleteBtn);
    buttonGroup.appendChild(cancelBtn);
    content.appendChild(buttonGroup);

    this.modalElement.appendChild(content);

    // Event handlers
    deleteBtn.addEventListener('click', () => this.close(true));
    cancelBtn.addEventListener('click', () => this.close(false));
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close(false);
    });

    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close(false);
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    document.body.appendChild(this.modalElement);

    // Focus the cancel button (safer default)
    cancelBtn.focus();
  }

  private close(confirmed: boolean): void {
    if (this.modalElement && this.modalElement.parentNode) {
      this.modalElement.parentNode.removeChild(this.modalElement);
    }
    this.modalElement = null;

    if (this.resolvePromise) {
      this.resolvePromise(confirmed);
      this.resolvePromise = null;
    }
  }
}
